import { useAppSSEOptional } from 'contexts/AppSSEContext';
import { AuthContext } from 'features/Auth/contexts/AuthContext';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { invalidateCache } from 'services/_infra/requestCache';
import { getAccessLevel, getUserPerms, validateRoutePerms } from 'services/perms/perms.service';

const PermsContext = createContext();

// Provider component
/** Access level 8 — the super user. Passes every permission-key check; the level list still applies. */
const SUPER_USER_ACCESS_LEVEL = 8;
/** Keys the server enforces for EVERY level (checkAccessPermsStrict) — no super-user bypass here either. */
const STRICT_PERMISSIONS = new Set([
  'CreateFemaAlert',
  'ViewFemaAlerts',
  'ManageFemaAlerts',
  'ManageFemaCredentials',
]);

export const PermsProvider = ({ children }) => {
  if (!children) return null;

  const authContext = useContext(AuthContext);
  const { accessToken } = authContext;
  const [data, setData] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [accessLevel, setAccessLevelState] = useState(null);
  const [roleId, setRoleId] = useState(null);
  const [maintenanceLevel, setMaintenanceLevelState] = useState(null);
  const [serviceManagerFlag, setServiceManagerFlagState] = useState(null);
  const [deploymentManagerFlag, setDeploymentManagerFlagState] = useState(null);

  // Load the current user's perms + access level. On the initial load we skip
  // if perms are already cached (`data`), but a `force` refresh (triggered by a
  // `role-perms-updated` SSE event) bypasses that guard and drops the request
  // cache so the refetch actually hits the network.
  const loadPerms = async ({ force = false } = {}) => {
    if (isFetching) return;
    if (!accessToken) return;
    if (!force && data) return;

    setIsFetching(true);

    if (force) {
      invalidateCache('getUserPerms');
      invalidateCache('checkAuthLevel');
    }

    // fetch both in parallel; order doesn’t matter for parsing
    const [accessResponse, permsResponse] = await Promise.all([
      getAccessLevel().catch(() => null),
      getUserPerms().catch(() => null),
    ]);

    const newAccessLevel = accessResponse?.data?.result?.group_id ?? null;
    if (newAccessLevel !== null) setAccessLevelState(newAccessLevel);

    if (permsResponse?.status === 200) {
      const raw = permsResponse.data?.perms;
      const permsData = typeof raw === 'string' ? JSON.parse(raw) : raw;
      setData(permsData ?? {});
      const parsedRoleId = parseInt(permsResponse.data?.roleId);
      if (!Number.isNaN(parsedRoleId)) setRoleId(parsedRoleId);
    }

    setIsFetching(false);
  };

  // Try immediately
  useEffect(() => {
    if (accessToken) loadPerms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // Live refresh: a `role-perms-updated` SSE event means a role definition (one
  // this user may hold) had its permissions changed, so this session's effective
  // perms may have shifted. Refetch and re-render permission-gated UI. The event
  // is workplace-scoped and a single admin save can fire a burst, so debounce.
  // `loadPermsRef` keeps the callback pointed at the latest closure without
  // re-subscribing on every render.
  const loadPermsRef = useRef(loadPerms);
  loadPermsRef.current = loadPerms;
  const sse = useAppSSEOptional();
  const subscribe = sse?.subscribe;

  useEffect(() => {
    if (!accessToken || !subscribe) return undefined;

    let timer = null;
    const unsubscribe = subscribe('role-perms-updated', (data) => {
      console.log('[role-perms-updated] received (perms handler):', data);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        console.log('[role-perms-updated] force-refreshing current-user perms', data);
        loadPermsRef.current?.({ force: true });
      }, 250);
    });

    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, [accessToken, subscribe]);

  const get = () => {
    return data;
  };

  const setAccessLevel = (level) => {
    setAccessLevelState(level);
  };

  const setMaintenanceLevel = (level) => {
    setMaintenanceLevelState(level);
  };

  const setServiceManagerFlag = (flag) => {
    setServiceManagerFlagState(flag);
  };

  const setDeploymentManagerFlag = (flag) => {
    setDeploymentManagerFlagState(flag);
  };

  function testPermission(accessLevelList, permissions = [], testData = null) {
    //Check Access Level against provided list
    const accessLevelCheck =
      accessLevelList && Array.isArray(accessLevelList) && accessLevelList.length > 0
        ? accessLevelList.includes(Number(accessLevel))
        : true;

    let permissionsCheck;

    // The SUPER USER (access level 8) bypasses permission keys — the backend's
    // checkAccessPerms already does (evaluateAccessPair: an admin level passes
    // before the keys are read), and this test used to demand the key in the
    // role blob regardless, so a new key nobody had assigned yet hid a control
    // from the super user while the server would have let the call through
    // (Mike, 2026-08-28: "I am a super user and should bypass permissions
    // altogether"). The explicit level list still applies — a control offered to
    // levels [10] only stays that way. The FEMA keys are the one exception: the
    // server enforces them STRICTLY for every level (an administrator must not
    // send a public alert by being an administrator), so the frontend must not
    // pretend otherwise.
    if (
      Number(accessLevel) === SUPER_USER_ACCESS_LEVEL &&
      !testData &&
      !permissions.some((p) => STRICT_PERMISSIONS.has(p))
    ) {
      return accessLevelCheck;
    }

    if (permissions.length > 0) {
      const permArr = permissions.map((permission) => {
        if (testData) {
          return testData[permission];
        }

        /**     (integrations and devices) **for now (this can)
        This defines exception permissions. These permissions aren't shown to the users.
            These permissions (in this case) return true if any permission that meets a naming scheme
            is true. This is to say "perm1 || perm2 || perm3 || etc..."
        */
        let filteredPerms;
        switch (permission) {
          case 'ViewConnections':
            filteredPerms = Object.keys(data)
              .filter((f) => {
                const regex = /^View[A-Za-z]+Connections$/;
                return regex.test(f);
              })
              .map((v) => v);
            return filteredPerms.length > 0
              ? filteredPerms.map((v) => data[v]).includes(true)
              : false;
          case 'Create-EditConnections':
            filteredPerms = Object.keys(data)
              .filter((f) => {
                const regex = /^Create-Edit[A-Za-z]+Connections$/;
                return regex.test(f);
              })
              .map((v) => v);
            return filteredPerms.length > 0
              ? filteredPerms.map((v) => data[v]).includes(true)
              : false;
          case 'DeleteConnections':
            filteredPerms = Object.keys(data)
              .filter((f) => {
                const regex = /^Delete[A-Za-z]+Connections$/;
                return regex.test(f);
              })
              .map((v) => v);
            return filteredPerms.length > 0
              ? filteredPerms.map((v) => data[v]).includes(true)
              : false;
          case 'ImportConnections':
            filteredPerms = Object.keys(data)
              .filter((f) => {
                const regex = /^Import[A-Za-z]+Connections$/;
                return regex.test(f);
              })
              .map((v) => v);
            return filteredPerms.length > 0
              ? filteredPerms.map((v) => data[v]).includes(true)
              : false;
          default:
            return data ? data[permission] : false;
        }
      });
      if (permArr.includes(undefined) || permArr.includes(null)) {
        return false;
      }
      permissionsCheck = !permArr.includes(false);
    } else if (permissions.length === 0) {
      permissionsCheck = accessLevelCheck;
    } else {
      permissionsCheck = false;
    }

    const finalResult = accessLevelCheck && permissionsCheck;
    return finalResult;
  }

  const validatePermissions = async (perms) => {
    try {
      const response = await validateRoutePerms(perms);
      return true;
    } catch (error) {
      if (error.response.status === 403) {
        return false;
      } else {
        return true;
      }
    }
  };

  const reset = () => {
    setData(null);
    setIsFetching(false);
    setAccessLevelState(null);
    setRoleId(null);
    setServiceManagerFlagState(null);
    setDeploymentManagerFlagState(null);
    setMaintenanceLevelState(null);
  };

  const isMaintenanceOperator = () => {
    //maintenanceLevel is true or false, not 1 or 0
    const result = maintenanceLevel || accessLevel === 8;
    return result;
  };

  const isServiceManager = () => {
    const result = serviceManagerFlag === true && accessLevel === 12 ? true : false;
    return result;
  };

  const isDeploymentManager = () => {
    // Mirrors isServiceManager: the flag matters at access level 12;
    // levels 8 and 13 always satisfy the gate via the standard access-level checks.
    // Backend sends the flag as 1/0, not true/false — App.jsx wraps with
    // Boolean() at hydration, but use a truthy check here as well so the
    // predicate stays correct even if a future call site forgets to wrap.
    return Boolean(deploymentManagerFlag) && accessLevel === 12;
  };

  // Full gate matching backend checkDeploymentManager: 8/13 always pass,
  // 12 passes when the flag is set. Anything else fails. Truthy check so
  // raw 1 / 0 values work directly (backend sends 1/0, not true/false).
  const hasDeploymentManagerAccess = () => {
    if (accessLevel === 8 || accessLevel === 13) return true;
    if (accessLevel === 12 && Boolean(deploymentManagerFlag)) return true;
    return false;
  };

  const contextValue = useMemo(
    () => ({
      get,
      setAccessLevel,
      setMaintenanceLevel,
      testPermission,
      validatePermissions,
      isFetching,
      data,
      reset,
      accessLevel,
      roleId,
      maintenanceLevel,
      isMaintenanceOperator,
      serviceManagerFlag,
      isServiceManager,
      setServiceManagerFlag,
      deploymentManagerFlag,
      isDeploymentManager,
      hasDeploymentManagerAccess,
      setDeploymentManagerFlag,
    }),
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [data, isFetching, accessLevel, roleId, maintenanceLevel],
  );
  return <PermsContext.Provider value={contextValue}>{children}</PermsContext.Provider>;
};

// Custom hook to use the context
export const usePerms = () => {
  const context = useContext(PermsContext);
  if (!context) {
    console.error('[perms] usePerms called outside of PermsProvider context!');
    throw new Error('usePerms must be used within a PermsProvider');
  }
  return context;
};

export { PermsContext };
